import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface UseCounterAnimationReturn {
  /** Current count being displayed */
  currentCount: number
  /** Previous count (only defined during animation) */
  oldCount: number | undefined
  /** Whether animation is currently playing */
  isAnimating: boolean
  /** Direction of the animation based on count change */
  animationDirection: 'up' | 'down' | undefined
  /** Ref to attach to the container element for measuring animation duration */
  containerRef: React.RefObject<HTMLDivElement>
  /** Callback to handle animation end events */
  onAnimationEnd: () => void
}

export function useCounterAnimation(count: number): UseCounterAnimationReturn {
  const [currentCount, setCurrentCount] = useState(count)
  const [oldCount, setOldCount] = useState<number | undefined>(undefined)
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationDirection, setAnimationDirection] = useState<'up' | 'down' | undefined>(undefined)

  const containerRef = useRef<HTMLDivElement>(null)
  const prevCountRef = useRef<number>(count)

  const onAnimationEnd = useCallback(() => {
    setIsAnimating(false)
    setOldCount(undefined)
    setAnimationDirection(undefined)
  }, [])

  useEffect(() => {
    const prevCount = prevCountRef.current
    prevCountRef.current = count

    if (prevCount === count) {
      setCurrentCount(count)
      return
    }

    const oldValue = isAnimating && oldCount !== undefined ? currentCount : prevCount
    const direction = count > oldValue ? 'up' : 'down'

    setOldCount(oldValue)
    setCurrentCount(count)
    setIsAnimating(true)
    setAnimationDirection(direction)
  }, [count, isAnimating, oldCount, currentCount])

  return useMemo(
    () => ({
      currentCount,
      oldCount,
      isAnimating,
      animationDirection,
      containerRef,
      onAnimationEnd,
    }),
    [currentCount, oldCount, isAnimating, animationDirection, onAnimationEnd],
  )
}
